/**
 * Camada de acesso HTTP + utilitarios de UI compartilhados.
 *
 * - `api`        : wrapper sobre axios (read/create/update/destroy) com unwrap
 *                  do envelope `{ data: ... }` retornado pelo BaseController.
 * - `sucesso/erro/aviso/confirmar` : helpers SweetAlert2 padronizados.
 * - Utilitarios de string/data/cor: PascalCase, gerarCorSequencial, etc.
 *
 * Consuma sempre via `import { api, sucesso, erro } from '@/helpers/common/api';`
 */

import Swal from 'sweetalert2';
import axios from 'axios';
import { registrarHeaderDeTenant } from '@/services/tenantAtivo';

axios.defaults.baseURL = process.env.VUE_APP_APIURL;
axios.defaults.withCredentials = true;

// Envia o espaço ativo em toda requisição. Um interceptor só, em vez de tocar
// nas centenas de chamadas espalhadas pelas telas. Ver `services/tenantAtivo`.
registrarHeaderDeTenant(axios);

const unwrap = (response) => {
  if (response && response.data && Object.prototype.hasOwnProperty.call(response.data, 'data')) {
    return response.data.data;
  }
  return response?.data;
};

class HttpAxios {
  async read(route, config = {}) {
    if (!route) { console.error('A rota e obrigatoria'); return false; }
    try {
      return unwrap(await axios.get(route, config));
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async create(route, payload, config = {}) {
    if (!route) { console.error('A rota e obrigatoria'); return false; }
    try {
      return unwrap(await axios.post(route, payload, config));
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async update(route, payload, config = {}) {
    if (!route) { console.error('A rota e obrigatoria'); return false; }
    try {
      return unwrap(await axios.put(route, payload, config));
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async destroy(route, config = {}) {
    if (!route) { console.error('A rota e obrigatoria'); return false; }
    try {
      return unwrap(await axios.delete(route, config));
    } catch (e) {
      console.error(e);
      return false;
    }
  }
}

export const api = new HttpAxios();

// ---------- Utilitarios de string ----------
export function PascalCase(string) {
  try {
    return string.charAt(0).toUpperCase() + string.slice(1);
  } catch (_e) {
    return string;
  }
}

export function removerTagsHtml(htmlString) {
  if (!htmlString || typeof htmlString !== 'string' || htmlString.trim().length === 0) return '';
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;
  return tempDiv.textContent || tempDiv.innerText || '';
}

// ---------- Helpers de notificacao (SweetAlert2) ----------
export function sucesso({
  icon = 'success',
  title = 'Tarefa realizada com sucesso!',
  text = false,
  showConfirmButton = false,
  timer = 1500,
} = {}) {
  Swal.fire({ icon, title, text, showConfirmButton, timer });
}

export function erro({
  icon = 'error',
  title = 'Erro ao realizar tarefa!',
  text = false,
  showConfirmButton = true,
  timer = false,
} = {}) {
  Swal.fire({ icon, title, text, showConfirmButton, timer });
}

export function aviso({
  icon = 'warning',
  title = 'Aviso!',
  text = false,
  showConfirmButton = true,
  timer = false,
} = {}) {
  Swal.fire({ icon, title, text, showConfirmButton, timer });
}

export function confirmar({
  confirmado = () => {},
  negado = () => {},
  options = {
    title: 'Voce esta certo disso?',
    text: 'Voce nao podera reverter esta acao!',
    icon: 'warning',
    showDenyButton: false,
    showCancelButton: true,
    confirmButtonText: 'Sim, tenho certeza!',
    customClass: {
      confirmButton: 'btn btn-success w-xs me-2',
      cancelButton: 'btn btn-danger w-xs',
      denyButton: 'btn btn-info w-xs me-2',
    },
    buttonsStyling: false,
    denyButtonText: 'Nao tenho certeza',
    showCloseButton: true,
  },
} = {}) {
  Swal.fire({ ...options }).then((result) => {
    if (result.isConfirmed) confirmado();
    else if (result.isDenied) negado();
  });
}

// ---------- Cores e helpers diversos ----------
export function gerarCorSequencial(index, saturacao = 0.8, luminosidade = 0.45) {
  const proporcaoAurea = 0.61803398875;
  const matiz = (index * proporcaoAurea) % 1;
  return `hsl(${matiz * 360}, ${saturacao * 100}%, ${luminosidade * 100}%)`;
}

export function obterClassePorLetra(nome) {
  const classes = ['bg-success', 'bg-primary', 'bg-secondary', 'bg-orange', 'bg-warning', 'bg-danger', 'bg-dark'];
  if (!nome || nome.length === 0) return classes[0];
  const letra = nome.substring(0, 1).toUpperCase();
  const pos = letra.charCodeAt(0) - 'A'.charCodeAt(0);
  if (pos < 0 || pos > 25) return classes[0];
  return classes[pos % classes.length];
}

export function getObjectByString(obj, path) {
  if (typeof path === 'string') path = path.split('.');
  if (!Array.isArray(path) || path.length === 0) return obj;
  return getObjectByString(obj[path[0]], path.slice(1));
}

export function convertDate(dataString) {
  const parts = String(dataString).split(' ');
  const dateParts = parts[0].split('-');
  const timeParts = (parts[1] || '00:00:00').split(':');
  return new Date(dateParts[0], dateParts[1] - 1, dateParts[2], timeParts[0], timeParts[1], timeParts[2]);
}

export function extractHour(timestamp) {
  const date = new Date(timestamp);
  return `${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`;
}

export function loading(status) {
  const preloader = document.getElementById('preloader');
  if (!preloader) return;
  if (status) {
    preloader.style.opacity = '1';
    preloader.style.visibility = 'visible';
  } else {
    preloader.style.opacity = '0';
    preloader.style.visibility = 'hidden';
  }
}
